// I think it would make sense for us to put this in our compendium project.
export default class FetchManager {
  constructor() {
    this.abortControllers = {};

    // default options
    this.options = {
      method: "GET",
      cache: "default",
      credentials: "same-origin",
      redirect: "follow",
      referrer: "client"
    };
  }

  /**
   *
   * Creates a new abort controller for each new request and returns it.
   * Unless the user passes in an existing signalId. Then it will return the existing controller for the request (this can be used to have one abort controller for multiple requests).
   * @param {string} signalId - A signal identifier that will be used to map to a controller object that belongs to an active request.
   * @returns { Object } returns the conttroler.
   */
  newAbortController(signalId) {
    if (!this.abortControllers[signalId]) {
      const abortController = new AbortController();

      this.abortControllers[signalId] = abortController;
    }

    return this.abortControllers[signalId];
  }

  /**
   * Aborts all active requests and cleans up all aborted controllers.
   */
  abortAll() {
    const abortControllerIds = Object.keys(this.abortControllers);

    for (let i = 0; i < abortControllerIds.length; i += 1) {
      if (this.abortControllers[abortControllerIds[i]]) {
        this.abortControllers[abortControllerIds[i]].abort();
        this.cleanUpAbortControllerById(abortControllerIds[i]);
      }
    }
  }

  /**
   * Aborts request(s) by signalId and cleans up all aborted controller(s).
   */
  abortById(signalId) {
    if (this.abortControllers[signalId]) {
      this.abortControllers[signalId].abort();
      this.cleanUpAbortControllerById(signalId);
    }
  }

  /**
   * Removes aborted controllers by signalId from the abortControllers object.
   */
  cleanUpAbortControllerById(signalId) {
    delete this.abortControllers[signalId];
  }

  /**
   *
   * @param {String} url - Url path for the request.
   * @param {Object} [options={}] - Fetch options https://developer.mozilla.org/en-US/docs/Web/API/Fetch_API/Using_Fetch#Supplying_request_options
   * @param {string} [signalId=random generated Id] - A signal identifier that will be used to map to an abort controller that belongs to the active request. If passes in an existing signalId, it will return the existing controller for this request (this can be used to have one abort controller for multiple requests).
   * @returns { Promise }
   */
  fetch(url, options = {}, signalId = `${new Date().getUTCMilliseconds()}`) {
    const { signal } = this.newAbortController(signalId);

    return fetch(url, {
      signal,
      ...this.options,
      ...options
    })
      .then(response => {
        if (!response.ok) {
          return response.json().then(error => {
            throw error;
          });
        }

        return response.json();
      })
      .finally(() => this.cleanUpAbortControllerById(signalId));
  }

  fetchWithAuth(url, options, signalId) {
    return this.fetch(
      url,
      {
        ...options,
        headers: {
          Authorization: `Bearer: ${options.auth.accessToken}`,
          "Content-Type": "application/json"
        }
      },
      signalId
    ).catch(
      error =>
        new Promise(async (resolve, reject) => {
          console.log("error:", error.status);

          if (
            error.status === 401 &&
            options.auth &&
            options.auth.refreshToken
          ) {
            try {
              console.log("Test try 401");

              // try to get new tokens if error status is 401.
              const newTokens = await this.fetch(
                `${options.auth.refreshTokenPath}`,
                {
                  method: "POST",
                  body: JSON.stringify({
                    refreshToken: options.auth.refreshToken
                  })
                }
              );

              console.log("Test newTokens:", newTokens);

              // use the new access token to make the call again.
              const afterRefreshResponce = await this.fetch(
                url,
                {
                  ...options,
                  headers: {
                    Authorization: `Bearer: ${newTokens.accessToken}`,
                    "Content-Type": "application/json"
                  }
                },
                signalId
              );

              // Utilities.setCognitoTokens(newTokens);
              resolve(afterRefreshResponce);
            } catch (afterRefreshError) {
              reject(afterRefreshError);
            }
          }

          return reject(error);
        })
    );
  }
}
